import { CUSTOMER_TIERS } from '../utils/consts';
import { ACCOUNT_AGENCY, ACCOUNT_PARTNER, ACCOUNT_TYPE_NAME_MAP, ROLE_RESTRICTED } from '../teamManagement/utils';
import { DEFAULT_USER_IMAGE } from './consts';

const ROLE_TYPE_TRANSLATION_KEYS = {
    Restricted: 'STATIC.TOPBAR_BADGES.RESTRICTED',
    Agency: 'STATIC.TOPBAR_BADGES.AGENCY',
    Partner: 'STATIC.TOPBAR_BADGES.PARTNER',
};

const MS_IN_DAY = 86400000;

export const getProfileDisplay = (userData, impersonatedOrganization) => {
    if (impersonatedOrganization) {
        return {
            name: userData.impersonated_org_company_display_name,
            image: userData.impersonated_org_company_logo || DEFAULT_USER_IMAGE,
        };
    }
    const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
    return {
        name: fullName || userData.email,
        image: userData.user_picture || userData.company_logo || null,
    };
};

export const getRoleBadge = userData => {
    const roleText = [ACCOUNT_AGENCY, ACCOUNT_PARTNER].includes(userData.account_type)
        ? ACCOUNT_TYPE_NAME_MAP[userData.account_type]
        : ACCOUNT_TYPE_NAME_MAP[userData.role_type];
    if (!roleText) {
        return null;
    }
    return { roleText, translationKey: ROLE_TYPE_TRANSLATION_KEYS[roleText] };
};

export const getPartnerBadgeImage = userData => userData.user_picture || userData.company_logo || DEFAULT_USER_IMAGE;

export const shouldShowAccountPlan = userData =>
    userData.role_type !== ROLE_RESTRICTED &&
    ![ACCOUNT_AGENCY, ACCOUNT_PARTNER].includes(userData.account_type) &&
    !!userData.is_sfdc_unique;

export const shouldShowTeamManagement = userData => !!userData.is_team_management_permitted;

export const shouldShow2FA = userData => !userData.should_block_app;

export const shouldShowAuditButton = userData =>
    !!userData.is_unified &&
    userData.role_type !== ROLE_RESTRICTED &&
    !(userData.feature_flags || []).includes('partner') &&
    !userData.should_block_app;

export const shouldShowAskSingularAI = userData =>
    (userData.feature_flags || []).includes('singular_ai') &&
    userData.role_type !== ROLE_RESTRICTED &&
    // Partner/agency accounts have no Analysis Chat route, so the handoff would dead-end.
    ![ACCOUNT_AGENCY, ACCOUNT_PARTNER].includes(userData.account_type) &&
    !userData.should_block_app;

export const showFreeTierCap = userData => userData.organization_tier === CUSTOMER_TIERS.FREE && !userData.trial;

export const isFreeTierOrTrial = userData => !!(userData.organization_tier === CUSTOMER_TIERS.FREE || userData.trial);

export const isStandardUnified = userData => userData.organization_tier === CUSTOMER_TIERS.STANDARD_UNIFIED;

export const calculateFreeTierInstalls = userData => {
    const { total_actual_conversions: actual, total_conversions_cap: cap } = userData;
    const conversionsLeft = actual ? cap - actual : cap;
    return conversionsLeft > 0 ? conversionsLeft.toLocaleString('en-US') : '0';
};

export const isAboutToMaxCap = userData => {
    const { total_actual_conversions: actual, total_conversions_cap: cap } = userData;
    return actual && cap ? actual / cap >= 0.95 : false;
};

// Keep later query parameters while removing the impersonated user from copied URLs.
export const stripImpersonationParam = url =>
    url.replace(/^(https?:\/\/[^/]+\/)\?user=[^&#]+(&)?/, (match, origin, amp) => (amp ? `${origin}?` : origin));

export const getTrialDaysLeft = trial => {
    const daysIntoTrial = Math.floor((Date.now() - new Date(trial.trial_start_date).getTime()) / MS_IN_DAY);
    const realDaysLeft = trial.trial_period - daysIntoTrial;
    return { realDaysLeft, daysLeft: Math.max(realDaysLeft, 0) };
};

export const getActiveWarnings = userData => ({
    sensitiveData:
        (!userData.is_blocked &&
            !!userData.is_admin &&
            userData.username !== userData.real_username &&
            ![3, 6].includes(userData.account_type)) ||
        userData.account_type === 5,
    blockedUserData: !!userData.is_admin && !!userData.is_blocked,
    highUsage: !!userData.has_high_usage_alerts && !userData.is_paused_alerts && !userData.is_blocked,
});
